import { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { addLogger, getBackendLogger } from '../loggers'
import { BackendLogger, EventConfig, LogEventParams } from '../types'

export const TrackingContext = createContext<{
  dispatchEvent: (params: LogEventParams) => void
  resolveUiKey: (key: string) => string
  setDefaultEventConfig: (config: EventConfig) => void
}>({
  dispatchEvent: () => {},
  resolveUiKey: () => '',
  setDefaultEventConfig: () => {},
})

export const TrackingProvider = ({
  children,
  loggers,
  resolveUiKey,
}: {
  children: React.ReactNode
  loggers?: Record<string, BackendLogger>
  resolveUiKey: (key: string) => string
}) => {
  const [defaultEventConfig, setDefaultEventConfig] = useState<EventConfig | undefined>()
  const defaultEventConfigRef = useRef<EventConfig | undefined>(defaultEventConfig)
  defaultEventConfigRef.current = defaultEventConfig

  const active = !!defaultEventConfig

  useEffect(() => {
    if (loggers) {
      Object.entries(loggers).forEach(([platform, logger]) => {
        addLogger(platform, logger)
      })
    }
  }, []) // only interpret loggers on mount, won't be reactive, mainly so that it can be declared inline

  const eventQueue = useRef<LogEventParams[]>([])

  useEffect(() => {
    if (!active) return

    if (eventQueue.current.length > 0) {
      eventQueue.current.forEach((event) => {
        dispatchEvent(event)
      })
      eventQueue.current = []
    }
  }, [active])

  const dispatchEvent = useCallback((event: LogEventParams) => {
    const eventConfig = defaultEventConfigRef.current
    if (!eventConfig) {
      eventQueue.current.push(event)
      return
    }

    let { key, type, name, action, additionalData, description, backends = ['amplitude'] } = event

    backends.forEach((backend) => {
      const config = eventConfig[backend.toLowerCase()]
      const whitelist = config?.whitelist || []

      // Validate eventKey against the backend-specific whitelist
      const isWhitelisted = whitelist.some((pattern) => {
        const regex = new RegExp(`^${pattern.replace(/\*/g, '.*')}$`)
        return regex.test(key)
      })

      if (!isWhitelisted) {
        console.debug(`Event "${key}" is not whitelisted for backend "${backend}". Skipping.`)
        return
      }

      const { eventName, ...rest } = additionalData

      const eventData = {
        key,
        type,
        name: eventName || name,
        description,
        action,
        ...rest,
      }

      const logger = getBackendLogger(backend)
      if (logger) {
        logger(key, eventData, eventName || name)
      } else {
        throw new Error(`Logger not found for backend: ${backend}`)
      }
    })
  }, [])

  return (
    <TrackingContext.Provider value={{ dispatchEvent: dispatchEvent, resolveUiKey, setDefaultEventConfig }}>
      {children}
    </TrackingContext.Provider>
  )
}
